/**
 * Request-a-swatch widget for Shopify stores.
 * Standalone, theme-neutral, no dependencies, no build step.
 * Drop in as a single <script src="..." defer></script> before </body>.
 *
 * The shopper builds a basket of swatches across several products, then
 * completes one request with their details. The basket lives in
 * localStorage, so it survives navigation between product pages.
 */
(function () {
	'use strict';

	// The one backend this widget talks to: the `swatch-request` edge function
	// in the Customer Service Hub Supabase project. No key travels with the
	// request and none belongs in this file — the function is public on
	// purpose and decides for itself whether to accept the caller, by checking
	// the browser's Origin against its own allowlist of store hostnames. A new
	// store therefore needs a row added there, not a change here.
	var SUPABASE_FUNCTION_URL =
		'https://midefaykvpnxqiggqvpy.supabase.co/functions/v1/swatch-request';

	/**
	 * @param {object} payload
	 * @returns {Promise<void>} rejects on any non-2xx or network failure, which
	 *   is what puts the modal into its error state with the basket intact.
	 */
	function submitSwatchRequest(payload) {
		return fetch(SUPABASE_FUNCTION_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload)
		}).then(function (r) {
			if (!r.ok) throw new Error('swatch request failed: ' + r.status);
		});
	}

	// `<script src="..." data-no-float defer>` suppresses the floating pill on
	// this store, leaving only the inline [data-swatch-button] buttons. Read
	// now, while the script is evaluating — `document.currentScript` is only
	// meaningful here, not later inside init().
	var NO_FLOAT = !!(document.currentScript && document.currentScript.hasAttribute('data-no-float'));

	var STORAGE_KEY = 'swatchBasket:v1';
	var COLOUR_OPTION_RE = /colou?r|fabric|finish|shade|material/i;
	var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	var CSS =
		'.sw-root,.sw-root *,.sw-btn{box-sizing:border-box;margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;line-height:1.4;letter-spacing:normal;text-transform:none;color:#1a1a1a}' +
		'.sw-root{position:relative;z-index:2147483000}' +
		'.sw-btn{background:#1a1a1a;color:#fff;border:0;border-radius:999px;padding:12px 20px;font-size:15px;font-weight:600;cursor:pointer}' +
		'.sw-btn:hover{background:#333}' +
		'.sw-launch{position:fixed;right:16px;bottom:16px;z-index:2147483000;box-shadow:0 2px 12px rgba(0,0,0,.25)}' +
		'.sw-inline{display:inline-block}' +
		'.sw-overlay{position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,.5);display:flex;align-items:flex-start;justify-content:center;overflow-y:auto;padding:24px 12px}' +
		'.sw-panel{position:relative;background:#fff;border-radius:12px;width:100%;max-width:560px;padding:24px}' +
		'.sw-close{position:absolute;top:8px;right:8px;background:transparent;border:0;font-size:24px;line-height:1;width:36px;height:36px;cursor:pointer;color:#666}' +
		'.sw-title{font-size:20px;font-weight:700;padding-right:36px;margin-bottom:4px}' +
		'.sw-sub{font-size:14px;color:#666;margin-bottom:16px}' +
		'.sw-legend{font-size:14px;font-weight:600;margin-bottom:8px}' +
		'.sw-colours{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:10px;margin-bottom:12px}' +
		'.sw-colour{display:block;border:1px solid #ddd;border-radius:8px;padding:8px;cursor:pointer;background:#fff;text-align:center}' +
		'.sw-colour.sw-on{border-color:#1a1a1a;box-shadow:0 0 0 1px #1a1a1a}' +
		'.sw-colour input{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}' +
		'.sw-thumb{width:100%;height:70px;object-fit:cover;border-radius:6px;display:block;background:#eee;border:0}' +
		'.sw-thumb-empty{width:100%;height:70px;border-radius:6px;background:#e6e3de}' +
		'.sw-cname{font-size:13px;margin-top:6px;display:block}' +
		'.sw-oos{font-size:11px;color:#888;display:block}' +
		'.sw-add{background:#1a1a1a;color:#fff;border:0;border-radius:8px;padding:11px 18px;font-size:15px;font-weight:600;cursor:pointer}' +
		'.sw-help{font-size:13px;color:#666;margin:0 0 14px}' +
		'.sw-ghost{width:100%;background:#fff;color:#1a1a1a;border:1px solid #1a1a1a;border-radius:8px;padding:13px;font-size:16px;font-weight:600;cursor:pointer;margin-top:10px}' +
		'.sw-ghost:hover{background:#f5f5f5}' +
		'.sw-hr{border-top:1px solid #eee;margin:20px 0}' +
		'.sw-group{margin-bottom:14px}' +
		'.sw-gname{font-size:13px;font-weight:600;color:#666;margin-bottom:6px}' +
		'.sw-item{display:flex;align-items:center;gap:10px;padding:6px 0}' +
		'.sw-item img,.sw-item .sw-ithumb{width:40px;height:40px;border-radius:6px;object-fit:cover;background:#e6e3de;flex:none;border:0}' +
		'.sw-iname{font-size:14px;flex:1}' +
		'.sw-remove{background:transparent;border:0;font-size:20px;line-height:1;width:32px;height:32px;cursor:pointer;color:#888}' +
		'.sw-total{font-size:14px;font-weight:600;margin:8px 0 14px}' +
		'.sw-field{margin-bottom:12px}' +
		'.sw-field label{display:block;font-size:13px;font-weight:600;margin-bottom:4px}' +
		'.sw-field input{width:100%;border:1px solid #ccc;border-radius:6px;padding:10px;font-size:15px;background:#fff;color:#1a1a1a}' +
		'.sw-field input:focus{outline:2px solid #1a1a1a;outline-offset:-1px}' +
		'.sw-row{display:flex;gap:12px}.sw-row .sw-field{flex:1}' +
		'.sw-hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}' +
		'.sw-err{color:#b00020;font-size:13px;margin-top:4px}' +
		'.sw-privacy{font-size:12px;color:#666;margin:12px 0}' +
		'.sw-submit{width:100%;background:#1a1a1a;color:#fff;border:0;border-radius:8px;padding:14px;font-size:16px;font-weight:600;cursor:pointer}' +
		'.sw-submit[disabled]{opacity:.6;cursor:default}' +
		'.sw-back{display:block;width:100%;background:transparent;border:0;color:#666;font-size:14px;text-decoration:underline;cursor:pointer;margin-top:12px;padding:4px}' +
		'.sw-done{text-align:center;padding:24px 0}' +
		'.sw-done h3{font-size:18px;font-weight:700;margin-bottom:8px}' +
		'.sw-done p{font-size:14px;color:#666}' +
		'@media (max-width:480px){.sw-row{display:block}.sw-panel{padding:18px}}';

	// ---------------------------------------------------------------- basket

	/** @type {Array<{productHandle: string, productTitle: string, sku: string, colour: string, image: string|null}>} */
	var basket = [];

	function loadBasket() {
		try {
			var raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
			basket = Array.isArray(raw) ? raw : [];
		} catch (e) {
			basket = [];
		}
	}

	function saveBasket() {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(basket));
		} catch (e) {
			/* private mode / full quota — the in-memory basket still works */
		}
	}

	/**
	 * @param {string} handle
	 * @param {string} colour
	 * @returns {number}
	 */
	function basketIndex(handle, colour) {
		for (var i = 0; i < basket.length; i++) {
			if (basket[i].productHandle === handle && basket[i].colour === colour) return i;
		}
		return -1;
	}

	// --------------------------------------------------------------- product

	/**
	 * Insert Shopify's `_200x` size suffix before the file extension.
	 * @param {string} src
	 * @returns {string}
	 */
	function thumbUrl(src) {
		var parts = src.split('?');
		return parts[0].replace(/(\.[a-z]+)$/i, '_200x$1') + (parts[1] ? '?' + parts[1] : '');
	}

	/**
	 * @param {{options: Array<{name: string, position: number}>}} product
	 * @returns {{name: string, position: number}|null}
	 */
	function findColourOption(product) {
		var opts = product.options || [];
		for (var i = 0; i < opts.length; i++) {
			if (COLOUR_OPTION_RE.test(opts[i].name)) return opts[i];
		}
		return opts.length === 1 ? opts[0] : null;
	}

	/**
	 * @param {object} product
	 * @param {{position: number}} option
	 * @returns {Array<{colour: string, sku: string, available: boolean, image: string|null}>}
	 */
	function buildColours(product, option) {
		var index = (option.position || 1) - 1;
		var seen = {};
		var out = [];
		(product.variants || []).forEach(function (v) {
			var colour = (v.options || [])[index];
			if (!colour || seen[colour]) return;
			seen[colour] = true;
			out.push({
				colour: colour,
				sku: v.sku || '',
				available: !!v.available,
				image: v.featured_image && v.featured_image.src ? thumbUrl(v.featured_image.src) : null
			});
		});
		return out;
	}

	// ------------------------------------------------------------------- dom

	/**
	 * @param {string} tag
	 * @param {string|null} [className]
	 * @param {string} [text]
	 * @returns {HTMLElement}
	 */
	function el(tag, className, text) {
		var node = document.createElement(tag);
		if (className) node.className = className;
		if (text != null) node.textContent = text;
		return node;
	}

	/**
	 * @param {string} name
	 * @param {string} label
	 * @param {boolean} required
	 * @param {string} [type]
	 * @returns {{wrap: HTMLElement, input: HTMLInputElement, error: HTMLElement}}
	 */
	function field(name, label, required, type) {
		var wrap = el('div', 'sw-field');
		var lab = el('label', null, label + (required ? ' *' : ''));
		lab.setAttribute('for', 'sw-' + name);
		var input = /** @type {HTMLInputElement} */ (el('input'));
		input.type = type || 'text';
		input.id = 'sw-' + name;
		input.name = name;
		var error = el('div', 'sw-err');
		wrap.appendChild(lab);
		wrap.appendChild(input);
		wrap.appendChild(error);
		return { wrap: wrap, input: input, error: error };
	}

	// ---------------------------------------------------------------- widget

	/** @type {{handle: string, title: string, colours: Array<object>}|null} */
	var current = null;
	/** @type {HTMLElement} */
	var root;
	/** Every button that opens the modal: the floating pill plus any inline ones. */
	/** @type {Array<HTMLButtonElement>} */
	var buttons = [];
	/** @type {HTMLElement|null} */
	var overlay = null;
	/** @type {HTMLElement|null} */
	var panelBody = null;

	function refreshButtons() {
		var label = basket.length ? 'Swatches (' + basket.length + ')' : 'Order a free swatch';
		var show = basket.length > 0 || !!current;
		buttons.forEach(function (b) {
			b.textContent = label;
			b.style.display = show ? '' : 'none';
		});
	}

	/** @param {KeyboardEvent} e */
	function onKey(e) {
		if (e.key === 'Escape') close();
	}

	function close() {
		if (!overlay) return;
		root.removeChild(overlay);
		overlay = null;
		panelBody = null;
		document.removeEventListener('keydown', onKey);
	}

	function open() {
		if (!overlay) {
			overlay = el('div', 'sw-overlay');
			overlay.addEventListener('click', function (e) {
				if (e.target === overlay) close();
			});

			var panel = el('div', 'sw-panel');
			overlay.appendChild(panel);

			var closeBtn = el('button', 'sw-close', '×');
			closeBtn.type = 'button';
			closeBtn.setAttribute('aria-label', 'Close');
			closeBtn.addEventListener('click', close);
			panel.appendChild(closeBtn);

			panelBody = el('div');
			panel.appendChild(panelBody);

			root.appendChild(overlay);
			document.addEventListener('keydown', onKey);
		}
		renderMain();
	}

	/**
	 * Replace the modal body with freshly built content.
	 * @param {Array<HTMLElement>} nodes
	 */
	function setBody(nodes) {
		if (!panelBody) return;
		panelBody.textContent = '';
		nodes.forEach(function (n) {
			/** @type {HTMLElement} */ (panelBody).appendChild(n);
		});
	}

	/** Colour picker (when on a product page) plus the basket. */
	function renderMain() {
		var nodes = [];
		nodes.push(el('div', 'sw-title', 'Free swatches'));
		nodes.push(el('div', 'sw-sub', 'Free fabric swatches, posted to you.'));

		if (current) {
			nodes.push(el('div', 'sw-legend', 'Pick colours for ' + current.title));
			var ticked = {};
			var grid = el('div', 'sw-colours');
			current.colours.forEach(function (c) {
				ticked[c.colour] = basketIndex(/** @type {object} */ (current).handle, c.colour) !== -1;
				var card = el('label', 'sw-colour' + (ticked[c.colour] ? ' sw-on' : ''));
				var box = /** @type {HTMLInputElement} */ (el('input'));
				box.type = 'checkbox';
				box.checked = ticked[c.colour];
				box.addEventListener('change', function () {
					ticked[c.colour] = box.checked;
					card.className = 'sw-colour' + (box.checked ? ' sw-on' : '');
				});
				card.appendChild(box);
				if (c.image) {
					var img = /** @type {HTMLImageElement} */ (el('img', 'sw-thumb'));
					img.src = c.image;
					img.alt = c.colour;
					card.appendChild(img);
				} else {
					card.appendChild(el('div', 'sw-thumb-empty'));
				}
				card.appendChild(el('span', 'sw-cname', c.colour));
				if (!c.available) card.appendChild(el('span', 'sw-oos', 'Made to order'));
				grid.appendChild(card);
			});
			nodes.push(grid);

			var add = el('button', 'sw-add', 'Add to swatch request');
			add.type = 'button';
			add.addEventListener('click', function () {
				var product = /** @type {{handle: string, title: string, colours: Array<object>}} */ (
					current
				);
				product.colours.forEach(function (c) {
					var at = basketIndex(product.handle, c.colour);
					if (ticked[c.colour] && at === -1) {
						basket.push({
							productHandle: product.handle,
							productTitle: product.title,
							sku: c.sku,
							colour: c.colour,
							image: c.image
						});
					} else if (!ticked[c.colour] && at !== -1) {
						basket.splice(at, 1);
					}
				});
				saveBasket();
				refreshButtons();
				renderMain();
			});
			nodes.push(add);
		}

		if (basket.length) {
			if (current) nodes.push(el('div', 'sw-hr'));
			nodes.push(el('div', 'sw-legend', 'Your swatch request'));

			var groups = [];
			basket.forEach(function (item) {
				var group = null;
				for (var i = 0; i < groups.length; i++) {
					if (groups[i].handle === item.productHandle) group = groups[i];
				}
				if (!group) {
					group = { handle: item.productHandle, title: item.productTitle, items: [] };
					groups.push(group);
				}
				group.items.push(item);
			});

			groups.forEach(function (group) {
				var box = el('div', 'sw-group');
				box.appendChild(el('div', 'sw-gname', group.title));
				group.items.forEach(function (item) {
					var row = el('div', 'sw-item');
					if (item.image) {
						var img = /** @type {HTMLImageElement} */ (el('img'));
						img.src = item.image;
						img.alt = item.colour;
						row.appendChild(img);
					} else {
						row.appendChild(el('div', 'sw-ithumb'));
					}
					row.appendChild(el('span', 'sw-iname', item.colour));
					var remove = el('button', 'sw-remove', '×');
					remove.type = 'button';
					remove.setAttribute('aria-label', 'Remove ' + item.colour + ' from ' + group.title);
					remove.addEventListener('click', function () {
						var at = basketIndex(item.productHandle, item.colour);
						if (at !== -1) basket.splice(at, 1);
						saveBasket();
						refreshButtons();
						renderMain();
					});
					row.appendChild(remove);
					box.appendChild(row);
				});
				nodes.push(box);
			});

			nodes.push(
				el('div', 'sw-total', basket.length + (basket.length === 1 ? ' swatch' : ' swatches'))
			);

			nodes.push(
				el(
					'p',
					'sw-help',
					"Keep browsing if you like — add swatches from as many products as you want, then complete your request when you're ready."
				)
			);

			var next = el('button', 'sw-submit', 'Complete request');
			next.type = 'button';
			next.addEventListener('click', renderForm);
			nodes.push(next);

			var backToProduct = el('button', 'sw-ghost', 'Back to product');
			backToProduct.type = 'button';
			backToProduct.addEventListener('click', close);
			nodes.push(backToProduct);
		}

		setBody(nodes);
	}

	/** Delivery details form. */
	function renderForm() {
		var nodes = [];
		nodes.push(el('div', 'sw-title', 'Where shall we send them?'));
		nodes.push(
			el(
				'div',
				'sw-sub',
				basket.length + (basket.length === 1 ? ' swatch' : ' swatches') + ' to post'
			)
		);

		var form = /** @type {HTMLFormElement} */ (el('form'));
		form.noValidate = true; // our own inline errors, not the browser's bubbles
		nodes.push(form);

		var fields = {
			name: field('name', 'Name', true),
			email: field('email', 'Email', true, 'email'),
			phone: field('phone', 'Phone', true, 'tel'),
			address1: field('address1', 'Address line 1', true),
			address2: field('address2', 'Address line 2', false)
		};
		['name', 'email', 'phone', 'address1', 'address2'].forEach(function (n) {
			form.appendChild(fields[n].wrap);
		});
		fields.city = field('city', 'Town/City', true);
		fields.postcode = field('postcode', 'Postcode', true);
		var row = el('div', 'sw-row');
		row.appendChild(fields.city.wrap);
		row.appendChild(fields.postcode.wrap);
		form.appendChild(row);

		var hp = el('div', 'sw-hp');
		var hpLabel = el('label', null, 'Company');
		hpLabel.setAttribute('for', 'sw-company');
		var hpInput = /** @type {HTMLInputElement} */ (el('input'));
		hpInput.type = 'text';
		hpInput.id = 'sw-company';
		hpInput.name = 'company';
		hpInput.tabIndex = -1;
		hpInput.autocomplete = 'off';
		hp.appendChild(hpLabel);
		hp.appendChild(hpInput);
		form.appendChild(hp);

		form.appendChild(
			el(
				'div',
				'sw-privacy',
				"We'll only use these details to send your swatches."
			)
		);

		var errBox = el('div', 'sw-err');
		form.appendChild(errBox);

		var submit = /** @type {HTMLButtonElement} */ (el('button', 'sw-submit', 'Request swatches'));
		submit.type = 'submit';
		form.appendChild(submit);

		var back = el('button', 'sw-back', 'Back to my swatches');
		back.type = 'button';
		back.addEventListener('click', renderMain);
		form.appendChild(back);

		/**
		 * @param {string} name
		 * @returns {string}
		 */
		function val(name) {
			return fields[name].input.value.trim();
		}

		form.addEventListener('submit', function (e) {
			e.preventDefault();
			errBox.textContent = '';
			var ok = true;
			['name', 'email', 'phone', 'address1', 'city', 'postcode'].forEach(function (n) {
				fields[n].error.textContent = val(n) ? '' : 'Please fill this in.';
				if (!val(n)) ok = false;
			});
			if (val('email') && !EMAIL_RE.test(val('email'))) {
				fields.email.error.textContent = 'Please enter a valid email address.';
				ok = false;
			}
			if (val('phone') && val('phone').replace(/[\s+-]/g, '').length < 7) {
				fields.phone.error.textContent = 'Please enter a valid phone number.';
				ok = false;
			}
			if (!basket.length) {
				errBox.textContent = 'Please choose at least one colour.';
				ok = false;
			}
			if (!ok) return;

			submit.disabled = true;
			submit.textContent = 'Sending…';

			var payload = {
				website: location.hostname,
				swatches: basket.map(function (item) {
					return {
						productHandle: item.productHandle,
						productTitle: item.productTitle,
						sku: item.sku,
						colour: item.colour
					};
				}),
				customer: {
					name: val('name'),
					email: val('email'),
					phone: val('phone'),
					address1: val('address1'),
					address2: val('address2'),
					city: val('city'),
					postcode: val('postcode')
				},
				honeypot: hpInput.value
			};

			submitSwatchRequest(payload).then(
				function () {
					basket = [];
					saveBasket();
					refreshButtons();
					var done = el('div', 'sw-done');
					done.appendChild(el('h3', null, 'Thanks — your swatches are on their way'));
					done.appendChild(
						el('p', null, "We'll post them to you shortly. Keep an eye on your inbox.")
					);
					setBody([done]);
				},
				function () {
					submit.disabled = false;
					submit.textContent = 'Request swatches';
					errBox.textContent = "Sorry, we couldn't send that. Please try again.";
				}
			);
		});

		setBody(nodes);
	}

	function mount() {
		var style = el('style');
		style.textContent = CSS;
		document.head.appendChild(style);

		root = el('div', 'sw-root');
		document.body.appendChild(root);

		if (!NO_FLOAT) {
			var launch = /** @type {HTMLButtonElement} */ (el('button', 'sw-btn sw-launch'));
			launch.type = 'button';
			launch.addEventListener('click', open);
			root.appendChild(launch);
			buttons.push(launch);
		}

		// Inline buttons: the theme drops <div data-swatch-button></div> wherever
		// it wants one, and we fill it with the same button.
		[].slice.call(document.querySelectorAll('[data-swatch-button]')).forEach(function (slot) {
			var inline = /** @type {HTMLButtonElement} */ (el('button', 'sw-btn sw-inline'));
			inline.type = 'button';
			inline.addEventListener('click', open);
			slot.appendChild(inline);
			buttons.push(inline);
		});

		refreshButtons();
		window.SwatchWidget = { open: open, close: close };
	}

	function init() {
		loadBasket();
		mount();

		// Test hook: the harness runs from file://, which has no /products/ path.
		var path = window.__SW_TEST_PATH__ || location.pathname;
		var match = /\/products\/([^/?#]+)/.exec(path);
		if (!match) return;

		fetch('/products/' + match[1] + '.js')
			.then(function (r) {
				if (!r.ok) throw new Error('bad status');
				return r.json();
			})
			.then(function (product) {
				var option = findColourOption(product);
				if (!option) return;
				var colours = buildColours(product, option);
				if (!colours.length) return;
				current = { handle: product.handle, title: product.title, colours: colours };
				refreshButtons();
				if (overlay) renderMain();
			})
			.catch(function () {
				/* stay silent — never break the host page */
			});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
